import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react/dist/ssr";
import PublicNavigation from "../components/PublicNavigation";
import PublicFooter from "../components/PublicFooter";
import { usePageMeta } from "../hooks/usePageMeta";
import "./FAQPage.css";

interface FAQ {
  question: string;
  answer: string | JSX.Element;
}

const FAQPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Add class to remove padding from main
  React.useEffect(() => {
    document.body.classList.add("homepage-route");
    return () => {
      document.body.classList.remove("homepage-route");
    };
  }, []);

  // SEO Meta Tags
  usePageMeta({
    title: "FAQ – CosaVorrei: lista regali privata per gruppi",
    description: "Domande frequenti su gruppi, eventi, liste private e prenotazioni: come funziona CosaVorrei nella tua cerchia di famiglia o amici.",
    ogTitle: "FAQ – CosaVorrei: lista regali privata per gruppi",
    ogDescription: "Tutto su gruppi, eventi, liste private e prenotazioni senza spoiler.",
    ogUrl: "https://www.cosavorrei.it/faq",
    ogImage: "https://www.cosavorrei.it/og-image.png",
    ogLocale: "it_IT",
    ogImageWidth: "1200",
    ogImageHeight: "630",
    twitterUrl: "https://www.cosavorrei.it/faq",
    twitterImage: "https://www.cosavorrei.it/og-image.png",
    canonicalUrl: "https://www.cosavorrei.it/faq",
  });

  const faqs: FAQ[] = [
    {
      question: "Cos'è CosaVorrei, in breve?",
      answer: "È uno spazio privato per la tua cerchia (famiglia, amici, colleghi) dove creare eventi (Natale, compleanni, ecc.), aggiungere i desideri e prenotare i regali senza spoiler e senza doppioni."
    },
    {
      question: "Chi può vedere le mie liste?",
      answer: "Solo i membri del tuo gruppo. Nessuno fuori dal gruppo può accedere alle liste o agli eventi."
    },
    {
      question: "Che differenza c'è tra \"evento a sorpresa\" ed \"evento normale\"?",
      answer: <>La modalità <strong>evento a sorpresa</strong> mantiene segreti i regali fino al giorno dell'evento. <br/>
      Chi riceverà i regali potrà pubblicare la suo lista solo quando sarà pronto e non vedrà quali regali sono stati prenotati. <br/>
      <strong>Se l'evento non è a sorpresa</strong>, chi riceverà i regali potrà vedere quali sono stati prenotati, ma non da chi. Potrà inoltre modificare o cancellare i regali non ancora prenotati.</>
    },
    {
      question: "Se prenoto un regalo, gli altri vedono la mia prenotazione?",
      answer: "Gli altri vedono che l'oggetto non è più disponibile (o che \"qualcuno ci sta pensando\"), ma non vedono il tuo nome."
    },
    {
      question: "Perché CosaVorrei non è una semplice wishlist?",
      answer: "Perché parte dal gruppo: stesse persone, più eventi nel tempo, liste private e prenotazioni coordinate. Niente link sparsi o wishlist duplicate ad ogni ricorrenza."
    },
    {
      question: "Posso decidere chi riceve i regali in un evento?",
      answer: "Sì. Chi gestisce il gruppo decide per ogni evento chi riceve i regali (es. solo i bambini) e se l'evento è a sorpresa o visibile."
    },
    {
      question: "E se qualcuno non usa l'app (bambini, nonni, ecc.)?",
      answer: "Puoi decidere chi gestirà la loro lista all'interno del gruppo. Tutto resta nello stesso posto."
    },
    {
      question: "Come prenoto un regalo? Cosa succede quando lo prenoto?",
      answer: "Una volta che stai visualizzando la lista di una persona, premi sul tasto \"Prenota\". Da quel momento in poi nessun'altro potrà vedere quel regalo o prenotarlo al posto tuo. La prenotazione prevede semplicemente che il regalo non sia più visibile agli altri. Non viene effettuata nessuna operazione di acquisto o altro."
    },
    {
      question: "Posso cambiare o annullare una prenotazione?",
      answer: <>Sì, dalla sezione <strong>Le mie prenotazioni</strong> puoi modificare o annullare quando vuoi.</>
    },
    {
      question: "Cosa succede se due persone prenotano lo stesso regalo?",
      answer: "Il sistema blocca i doppioni: quando un articolo è prenotato, risulta non disponibile agli altri."
    },
    {
      question: "Posso creare più gruppi?",
      answer: "Certo, puoi creare quanti gruppi vuoi dalla pagina /create-group e mettendo la tua stessa email. Al login poi ti verrà richiesto a quale gruppo vuoi accedere. Una volta dentro in un gruppo, nel menu è possibile premere su \"Cambia gruppo\" per passare ad un altro."
    },
    {
      question: "Quanto costa?",
      answer: "CosaVorrei è gratuito. Se in futuro introdurremo piani premium, lo comunicheremo in modo chiaro."
    },
    {
      question: "Come guadagnate da questo servizio?",
      answer: "Questo servizio è stato pensato per essere gratuito ed aiutare le persone a gestire senza stress i loro regali e desideri. Al momento non è previsto nessun modo per guadagnare da CosaVorrei."
    },
    {
      question: "Serve creare un account?",
      answer: "Sì, serve un account per entrare nel gruppo, vedere/gestire la propria lista e prenotare i regali. Se qualcuno non usa l'app (bambini, nonni, ecc.) puoi decidere chi gestirà la loro lista all'interno del gruppo."
    },
    {
      question: "Posso cancellare il mio account e i miei dati?",
      answer: <>Certo. Dalle impostazioni puoi richiedere la cancellazione dei dati. Dettagli in <Link to="/privacy">Privacy</Link>.</>
    },
    {
      question: "Come proteggete i dati?",
      answer: <>Le liste e le prenotazioni sono visibili solo al gruppo. Usiamo buone pratiche di sicurezza applicativa e non vendiamo i dati a terzi. Info complete in <Link to="/privacy">Privacy e Cookie</Link>.</>
    },
    {
      question: "C'è un'app mobile?",
      answer: "Il sito è responsive e funziona bene su smartphone e tablet. Stiamo valutando app dedicate."
    },
    {
      question: "Ho altre domande o un problema",
      answer: <>Scrivici a <a href="mailto:info@cosavorrei.it">info@cosavorrei.it</a>. Rispondiamo appena possibile.</>
    }
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (typeof faq.answer === 'string' && faq.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // JSON-LD Schema for FAQPage
  React.useEffect(() => {
    // Remove any existing FAQPage scripts to avoid duplicates
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'FAQPage') {
          script.remove();
        }
      } catch (e) {
        // Ignore parsing errors
      }
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema'; // Add unique ID
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Chi può vedere le mie liste?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solo i membri del tuo gruppo. Nessuno fuori dal gruppo può accedere alle liste o agli eventi."
          }
        },
        {
          "@type": "Question",
          "name": "Che differenza c'è tra evento a sorpresa ed evento normale?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "La modalità evento a sorpresa mantiene segreti i regali fino al giorno dell'evento. Chi riceverà i regali potrà pubblicare la suo lista solo quando sarà pronto e non vedrà quali regali sono stati prenotati. Se l'evento non è a sorpresa, chi riceverà i regali potrà vedere quali sono stati prenotati, ma non da chi. Potrà inoltre modificare o cancellare i regali non ancora prenotati."
          }
        },
        {
          "@type": "Question",
          "name": "Se prenoto un regalo gli altri vedono la mia prenotazione?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Gli altri vedono solo che l'oggetto non è più disponibile (o che qualcuno ci sta pensando), ma non vedono il tuo nome."
          }
        },
        {
          "@type": "Question",
          "name": "Perché CosaVorrei non è una semplice wishlist?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Perché parte dal gruppo: stesse persone, più eventi nel tempo, liste private e prenotazioni coordinate senza link sparsi o doppioni."
          }
        },
        {
          "@type": "Question",
          "name": "Posso decidere chi riceve i regali in un evento?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sì. Per ogni evento chi gestisce il gruppo decide i destinatari (es. solo i bambini) e se l'evento è a sorpresa o visibile."
          }
        },
        {
          "@type": "Question",
          "name": "E se qualcuno non usa l'app (bambini, nonni)?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Puoi decidere chi gestirà la loro lista all'interno del gruppo. Tutto resta nello stesso posto."
          }
        },
        {
          "@type": "Question",
          "name": "Come prenoto un regalo? Cosa succede quando lo prenoto?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Una volta che stai visualizzando la lista di una persona, premi sul tasto Prenota. Da quel momento in poi nessun'altro potrà vedere quel regalo o prenotarlo al posto tuo. La prenotazione prevede semplicemente che il regalo non sia più visibile agli altri. Non viene effettuata nessuna operazione di acquisto o altro."
          }
        },
        {
          "@type": "Question",
          "name": "Posso cambiare o annullare una prenotazione?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sì, dalla sezione Le mie prenotazioni puoi modificare o annullare quando vuoi."
          }
        },
        {
          "@type": "Question",
          "name": "Come evitate i doppioni?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Quando un articolo viene prenotato, risulta non disponibile agli altri membri del gruppo."
          }
        },
        {
          "@type": "Question",
          "name": "Posso creare più gruppi?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Certo, puoi creare quanti gruppi vuoi dalla pagina /create-group e mettendo la tua stessa email. Al login poi ti verrà richiesto a quale gruppo vuoi accedere. Una volta dentro in un gruppo, nel menu è possibile premere su Cambia gruppo per passare ad un altro."
          }
        },
        {
          "@type": "Question",
          "name": "Quanto costa?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CosaVorrei è gratuito. Eventuali piani premium saranno comunicati con trasparenza."
          }
        },
        {
          "@type": "Question",
          "name": "Come guadagnate da questo servizio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Questo servizio è stato pensato per essere gratuito ed aiutare le persone a gestire senza stress i loro regali e desideri. Al momento non è previsto nessun modo per guadagnare da CosaVorrei."
          }
        },
        {
          "@type": "Question",
          "name": "Posso cancellare il mio account e i miei dati?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sì. Dalle impostazioni puoi richiedere la cancellazione dei dati. Dettagli in Privacy e Cookie."
          }
        },
        {
          "@type": "Question",
          "name": "Come vi contatto?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Scrivi a info@cosavorrei.it. Rispondiamo appena possibile."
          }
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('faq-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <div className="faq-page">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="faq-hero">
        <h1 className="faq-hero-title">
          Domande <span className="highlight">Frequenti</span>
        </h1>
        <p className="faq-hero-subtitle">
          Tutto quello che devi sapere su CosaVorrei
        </p>
      </section>

      {/* Search Section */}
      <section className="faq-search-section">
        <div className="faq-search-container">
          <div className="faq-search-box">
            <MagnifyingGlass size={24} weight="regular" />
            <input
              type="text"
              placeholder="Cerca nelle domande..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="faq-search-input"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="faq-content">
        <div className="faq-container">
          {filteredFaqs.length === 0 ? (
            <div className="faq-no-results">
              <p>Nessuna domanda trovata per "{searchTerm}"</p>
            </div>
          ) : (
            filteredFaqs.map((faq) => (
              <div key={faq.question} className={`faq-item ${openIndex === faqs.indexOf(faq) ? 'open' : ''}`}>
                <button
                  className="faq-question-button"
                  onClick={() => toggleFaq(faqs.indexOf(faq))}
                  aria-expanded={openIndex === faqs.indexOf(faq)}
                >
                  <h2 className="faq-question">{faq.question}</h2>
                  <CaretDown 
                    size={24} 
                    weight="bold" 
                    className="faq-caret"
                  />
                </button>
                <div className="faq-answer-wrapper">
                  <div className="faq-answer">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="faq-cta">
        <div className="faq-cta-container">
          <h2 className="faq-cta-title">Pronto a iniziare?</h2>
          <p className="faq-cta-subtitle">
            Crea il tuo gruppo e scopri quanto è semplice organizzare regali senza stress.
          </p>
          <Link to="/create-group" className="faq-cta-button">
            Crea il tuo gruppo
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default FAQPage;
